import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  callClaimCertificate, getCheckpointResponse, getEnrolledCourses, getLessons, getMyCertificate, getMyHomework,
  getProgressForLessons, type Certificate,
} from "../lib/firebase";
import { AMF_MODULES, HOMEWORK } from "../lib/homework";
import { AMF_MODULE_COURSE, amfStatus, type LessonLite, type ModuleStatus } from "../lib/journey";
import { DEFAULT_AVATAR } from "../lib/avatar";
import { PixelAvatar } from "../components/PixelAvatar";

/** AI Money Factory 8-bit map (Dew, 2026-10-20): Module 1-7 as stages, the student's character stands on
 *  the stage they are on, the 🏆 at the end is the certificate (claimable once every stage is done). */
export function JourneyPage() {
  const { user, isAdmin, profile } = useAuth();
  const [status, setStatus] = useState<ModuleStatus[] | null>(null);
  const [owned, setOwned] = useState<Set<string>>(new Set());
  const [cert, setCert] = useState<Certificate | null>(null);
  const [name, setName] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const courseIds = [...new Set(Object.values(AMF_MODULE_COURSE))];
        const [courses, hw, mine, perCourse] = await Promise.all([
          getEnrolledCourses(user.uid),
          getMyHomework(user.uid),
          getMyCertificate(user.uid, "amf"),
          Promise.all(courseIds.map((c) => getLessons(c).catch(() => []))),
        ]);
        const lessons: LessonLite[] = perCourse.flat();
        const progress = await getProgressForLessons(user.uid, lessons.map((l) => l.id));
        const quizzes = await Promise.all(AMF_MODULES.map((m) =>
          getCheckpointResponse(user.uid, m.courseId, `amf-quiz-m${m.n}`).then((r) => (r && !r.skipped ? m.n : 0)).catch(() => 0)));
        const sentModules = new Set(HOMEWORK.filter((h) => hw.get(h.id)?.text || hw.get(h.id)?.link).map((h) => h.module));
        const done = new Set([...progress].filter(([, p]) => p.completedAt).map(([id]) => id));
        setOwned(new Set(courses.map((c) => c.id)));
        setCert(mine);
        setStatus(amfStatus(lessons, done, new Set(quizzes.filter(Boolean)), sentModules));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [user]);

  if (!user) return null;
  if (error && !status) return <main><p className="error">{error}</p></main>;
  if (!status) return <p style={{ padding: "3rem", color: "var(--c-text-muted)" }}>กำลังโหลดแผนที่…</p>;

  const can = (n: number) => isAdmin || owned.has(AMF_MODULE_COURSE[n]);
  const allDone = status.every((m) => m.done);
  const here = status.find((m) => !m.done && can(m.n))?.n ?? 0;
  const left = status.filter((m) => !m.done).length;
  const avatar = profile?.avatar ?? DEFAULT_AVATAR;

  async function claim() {
    setClaiming(true);
    setError(null);
    try {
      await callClaimCertificate(name);
      setCert(await getMyCertificate(user!.uid, "amf"));
      setClaiming(false);
    } catch (e) {
      setError(e instanceof Error ? e.message.replace(/^.*?: /, "") : String(e));
      setClaiming(false);
    }
  }

  return (
    <main className="container-tight journey">
      <Link to="/dashboard?group=AI%20Money%20Factory" className="page-header__back">← AI Money Factory</Link>
      <h1>แผนที่ AI Money Factory</h1>
      <p className="journey__sub">เดินให้ครบ 7 ด่าน ด่านละ: เรียนให้ได้ 80% + ทำ Quiz + ส่งการบ้าน แล้วไปขอใบเซอร์ที่ปลายทาง (ดิวดูงานแล้วอนุมัติให้)</p>

      <ol className="journey__path">
        {status.map((m, i) => {
          const mod = AMF_MODULES.find((x) => x.n === m.n)!;
          const state = !can(m.n) ? "locked" : m.done ? "done" : m.n === here ? "here" : "todo";
          return (
            <li key={m.n} className={`journey__stage journey__stage--${state} ${i % 2 ? "is-right" : "is-left"}`}>
              <div className="journey__node">
                {state === "here" ? <PixelAvatar avatar={avatar} size={52} /> : state === "done" ? "✓" : state === "locked" ? "🔒" : m.n}
              </div>
              <div className="journey__card">
                <strong>ด่าน {m.n} · {mod.title}</strong>
                {state === "locked" ? (
                  <small>อยู่ในแพ็กที่คุณยังไม่มี</small>
                ) : (
                  <>
                    <span className="journey__checks">
                      <span className={m.lessonsTotal && m.lessonsDone >= Math.ceil(m.lessonsTotal * 0.8) ? "ok" : ""}>บทเรียน {m.lessonsDone}/{m.lessonsTotal}</span>
                      <span className={m.quiz ? "ok" : ""}>{m.quiz ? "✓" : "○"} Quiz</span>
                      <span className={m.homework ? "ok" : ""}>{m.homework ? "✓" : "○"} การบ้าน</span>
                    </span>
                    {!m.done && <span className="journey__links">
                      <Link to={`/courses/${mod.courseId}`}>ไปเรียน →</Link>
                      <Link to={`/homework?m=${m.n}`}>Quiz + การบ้าน →</Link>
                    </span>}
                  </>
                )}
              </div>
            </li>
          );
        })}
        <li className={`journey__stage journey__stage--goal ${allDone ? "is-open" : ""}`}>
          <div className="journey__node">🏆</div>
          <div className="journey__card">
            <strong>ใบประกาศนียบัตร AI Money Factory</strong>
            {cert?.status === "approved" ? (
              <Link to={`/cert/${cert.id}`} className="btn-primary">ดูใบเซอร์ของคุณ →</Link>
            ) : cert?.status === "pending" ? (
              <small>⏳ ส่งคำขอแล้ว รอดิวดูงานและอนุมัติ (ชื่อบนใบเซอร์: {cert.name}) เดี๋ยวกลับมาที่หน้านี้อีกทีนะครับ</small>
            ) : allDone ? (
              <>
                {cert?.status === "rejected" && <p className="hw-redo">ดิวขอให้แก้ก่อน{cert.note ? `: ${cert.note}` : ""} แก้เสร็จแล้วขอใหม่ได้เลย</p>}
                <small>ผ่านครบทุกด่านแล้ว! ใส่ชื่อที่จะพิมพ์บนใบเซอร์ (แก้ไม่ได้หลังดิวอนุมัติ)</small>
                <input value={name} maxLength={60} placeholder="ชื่อ นามสกุล" onChange={(e) => setName(e.target.value)} />
                <button type="button" className="btn-primary" disabled={claiming || !name.trim()} onClick={claim}>
                  {claiming ? "กำลังส่งคำขอ…" : "ขอใบเซอร์ 🏆"}
                </button>
              </>
            ) : (
              <small>อีก {left} ด่าน ก็ได้รับใบเซอร์แล้ว</small>
            )}
            {error && <p className="error">{error}</p>}
          </div>
        </li>
      </ol>
    </main>
  );
}
